import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  AccountCredentialIntegrationType,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface YardiVoyagerMetadata {
  configId: string // Yardi instance identifier
  defaultProperty?: string
  laborEmployee?: string // Fallback Labor-tab assignee when a handoff has no on-call assignee
}

/**
 * Yardi Voyager Integration API Client
 * Handles all Yardi Voyager-specific API operations
 */
export class YardiVoyagerClient extends BaseApiClient {
  /**
   * Create account credentials for Yardi Voyager
   */
  async createCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: YardiVoyagerMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_VOYAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error creating Yardi Voyager account credentials',
      },
    )
  }

  /**
   * [ADMIN] Create account credentials for Yardi Voyager for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: YardiVoyagerMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_VOYAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error creating admin Yardi Voyager account credentials',
      },
    )
  }

  /**
   * Update account credentials for Yardi Voyager
   */
  async updateCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: YardiVoyagerMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_VOYAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error updating Yardi Voyager account credentials',
      },
    )
  }

  /**
   * [ADMIN] Update account credentials for Yardi Voyager for a specific company
   */
  async updateAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: YardiVoyagerMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.YARDI_VOYAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating admin Yardi Voyager account credentials',
      },
    )
  }

  /**
   * Disconnect Yardi Voyager integration
   */
  async disconnect(): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
          { integrationType: AccountCredentialIntegrationType.YARDI_VOYAGER },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting Yardi Voyager integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect Yardi Voyager integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
      { integrationType: AccountCredentialIntegrationType.YARDI_VOYAGER },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin Yardi Voyager integration',
      },
    )
  }
}

// Export singleton instance
export const yardiVoyagerClient = new YardiVoyagerClient()
